import { Activity, useEffect, useState } from 'react';
import { components } from 'react-select';

import Text from '@ui/text/text/text';
import { Speaker } from '@/types/speakers.types';
import { useAppSelector } from '@/store/hooks';
import { getIsLoadingSpeakers, getSpeakers } from '@/store/slices/speakers/selectors';
import useLoadSpeakers from '@/connection/hooks/use-load-speakers';
import OptionalComponent from '@ui/optional-component/optional-component';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import EditStateSelect from '@ui/edit-state-wrapper/edit-state-select';
import { assertIsCustomReactOption } from '@/utils/assertions/assert-is-custom-react-option';
import CastAndCrewOption from './cast-and-crew-option';
import Container from '@ui/container/container';

type CastAndCrewSelectProps = {
  defaultSpeakers?: { label: string; value: number; meta?: Speaker }[];
  speakers?: { label: string; value: number; meta?: Speaker }[];
  onMenuClose: (selectedSpeakers: { label: string; value: number; meta?: Speaker }[]) => void;
  disabled?: boolean;
};

const CastAndCrewSelect = ({
  disabled,
  defaultSpeakers,
  speakers: propsSpeakers,
  onMenuClose,
}: CastAndCrewSelectProps) => {
  useLoadSpeakers({ skipIfSpeakersExist: true });

  const loadingSpeakers = useAppSelector(getIsLoadingSpeakers);
  const allSpeakers = useAppSelector(getSpeakers);

  const [selectedSpeakers, setSelectedSpeakers] = useState<TReactSelectOption<string, number, Speaker>[]>([]);

  useEffect(() => {
    setSelectedSpeakers(defaultSpeakers || []);
  }, [defaultSpeakers]);

  useEffect(() => {
    if (propsSpeakers) {
      setSelectedSpeakers(propsSpeakers);
    }
  }, [propsSpeakers]);

  return (
    <div data-component="CastAndCrewSelect">
      <EditStateSelect
        disabled={disabled}
        isLoading={loadingSpeakers}
        noOptionsMessage="No members found."
        Label={() => (
          <Container width="100%" className="flex-1">
            <Text className="L3 margin-b-8" color="var(--gray-300)">
              Cast{' '}
              <OptionalComponent display={!!selectedSpeakers.length}>
                <>
                  • {selectedSpeakers.length} MEMBER{selectedSpeakers.length > 1 ? 'S' : ''}
                </>
              </OptionalComponent>
            </Text>
          </Container>
        )}
        placeholder={!disabled ? 'Add members' : ''}
        value={selectedSpeakers}
        options={(allSpeakers || []).map((s) => ({
          label: `${s.first_name} ${s.last_name}`.trim(),
          value: s.speaker,
          meta: s,
        }))}
        onMenuClose={() => onMenuClose(selectedSpeakers)}
        onChange={(newValue) => {
          setSelectedSpeakers(newValue as TReactSelectOption<string, number, Speaker>[]);
        }}
        Components={{
          MenuList: ({ children, ...rest }) => {
            return (
              <components.MenuList {...rest}>
                {/* Activity wrapper just to ensure we don't try and render speaker lists when not neceessary */}
                <Activity mode={rest.selectProps.menuIsOpen ? 'visible' : 'hidden'}>{children}</Activity>
              </components.MenuList>
            );
          },
          Option: (props) => {
            const option = props.data;
            try {
              assertIsCustomReactOption(option);
            } catch (error) {
              console.error('Error asserting custom react option', error);
              return <></>;
            }

            return (
              <components.Option
                {...props}
                innerProps={{
                  ...props.innerProps,
                  style: { padding: 0, color: 'var(--gray-100)' },
                }}
              >
                <CastAndCrewOption option={option} isSelected={props.isSelected} />
              </components.Option>
            );
          },
        }}
      />
    </div>
  );
};

export default CastAndCrewSelect;
